import React, { useState, useEffect } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isArray from 'lodash/isArray';
import { CSSTransition } from 'react-transition-group';
import LoadMask from '../../components/LoadMask';
import {ANIMATION_TIMEOUT_APPEAR} from '../../constants/animation';
import {CHECKOUT_METHOD, CHECKOUT_PRODUCT} from '../../constants/checkout';
import ENDPOINT from '../../constants/endpoints';
import ERROR_FIELD from '../../constants/error-fields';
import ROUTE from '../../constants/route';
import useApi from '../../hooks/useApi';
import PageLayout from '../../layouts/PageLayout';
import ConfirmDialog from '../../scenes/Dialog/components/ConfirmDialog';
import ResponseErrorDialog from '../../scenes/Dialog/components/ResponseErrorDialog';
import Form from '../../scenes/Form';
import CheckoutForm from '../../scenes/Form/components/CheckoutForm';
import Sidebar from '../../scenes/Sidebar';
import './styles.css';

const Checkout = props => {
  const {
    history,
    match: {
      params: { type = null },
    },
    globalActions: { loadUserState },
  } = props;

  const api = useApi();

  const [isFetching, setFetching] = useState();
  const [params, setParams] = useState({
    productName: '---',
    tax: '---',
    price: '---',
    total: '---',
  });
  const [options, setOptions] = useState();
  const [fields, setFields] = useState();
  const [returnUrl, setReturnUrl] = useState();
  const [checkoutButtonParams, setCheckoutButtonParams] = useState();

  useEffect(() => {
    const {
      location: { state },
    } = history;

    let returnUrl = state && state.returnUrl ? state.returnUrl : null;

    let pid = null;
    switch (type) {
      case 'inbounds':
        pid = CHECKOUT_PRODUCT.INBOUNDS_5;
        returnUrl = returnUrl || ROUTE.CANDIDATE_ACCOUNT;
        break;

      case 'premium':
        pid = CHECKOUT_PRODUCT.PREMIUM;
        returnUrl = returnUrl || ROUTE.CANDIDATE_ACCOUNT;
        break;

      case 'premium-prep':
        pid = CHECKOUT_PRODUCT.PREMIUM_WITH_PREPARATORY;
        returnUrl = returnUrl || ROUTE.CANDIDATE_ACCOUNT;
        break;

      case 'prep':
        pid = CHECKOUT_PRODUCT.PREPARATORY_FOR_PREMIUM;
        returnUrl = returnUrl || ROUTE.CANDIDATE_ACCOUNT;
        break;

      case 'credit':
        pid = CHECKOUT_PRODUCT.RESTRICTED_FIRMS_JOB_CREDITS;
        returnUrl = returnUrl || ROUTE.SEARCH_PROCESS_MANAGE;
        break;

      case 'credits':
        pid = CHECKOUT_PRODUCT.RESTRICTED_FIRMS_JOB_CREDITS_3;
        returnUrl = returnUrl || ROUTE.SEARCH_PROCESS_MANAGE;
        break;

      default:
        history.replace(ROUTE.ERROR_404);
        break;
    }

    setReturnUrl(returnUrl);
    setFetching(true);

    api.get(
      ENDPOINT.OTHER.CHECKOUT,
      { pid },
      {
        handleNotFound: () => history.replace(ROUTE.ERROR_404),
        handleForbidden: () => history.replace(ROUTE.ERROR_404),
        handleError: response => ResponseErrorDialog({ response }),
        handleSuccess: data => {
          setParams({ ...data.params });
          setFields({
            ...data.fields,
            pid,
            returnUrl,
          });
          setOptions({ ...data.options });
          setCheckoutButtonParams({ ...data.checkoutButton });
        },
        handleComplete: () => {
          setFetching(false);
        },
      }
    );
  }, []);

  const handleRemoveCard = (id, callback) => {
    ConfirmDialog({
      dismissWhenClose: true,
      bodyRender: <p>Are you sure you want to delete this card?</p>,
    }).then(() => {
      setFetching(true);
      api.post(
        ENDPOINT.ACTION.CHECKOUT_REMOVE_CARD,
        { id },
        {
          handleError: response => ResponseErrorDialog({ response }),
          handleSuccess: () => {
            const nextOptions = cloneDeep(options);
            nextOptions.storedCreditCards = nextOptions.storedCreditCards.filter(
              card => card.value !== id
            );
            setOptions(nextOptions);
            callback(
              !!nextOptions.storedCreditCards.length
                ? nextOptions.storedCreditCards[0]
                : null
            );
          },
          handleComplete: () => setFetching(false),
        }
      );
    });
  };

  const handleCheckout = (values, { setSubmitting, setErrors }) => {
    if (values.method === CHECKOUT_METHOD.PAYPAL) {
      const params = {
        ...checkoutButtonParams,
        email: values.email,
        first_name: values.firstName,
        last_name: values.lastName,
        return: checkoutButtonParams.return + returnUrl,
        cancel_return: checkoutButtonParams.cancel_return + returnUrl,
      };

      const form = document.createElement('form');
      form.setAttribute('method', 'POST');
      form.setAttribute('action', params.action_url);
      document.body.appendChild(form);

      for (let field in params) {
        if (field !== 'action_url') {
          const input = document.createElement('input');
          input.setAttribute('name', field);
          input.setAttribute('value', params[field]);
          form.appendChild(input);
        }
      }
      form.submit();
    } else {
      api.post(ENDPOINT.ACTION.CHECKOUT_PAY, values, {
        handleError: response => {
          if (response.data && response.data.fields) {
            const obj = {};
            for (let [key, value] of Object.entries(response.data.fields)) {
              const error = isArray(value) ? value[0] : value;
              obj[key] = ERROR_FIELD[error] || `${error}`;
            }
            setErrors(obj);
          } else {
            ResponseErrorDialog({ response });
          }
        },
        handleSuccess: () => {
          loadUserState();
          history.replace(returnUrl);
        },
        handleComplete: () => setSubmitting(false),
      });
    }
  };

  return (
    <PageLayout
      pageClass="checkout"
      pageTitle="Checkout"
      mainContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <Form>
            <div style={{ position: 'relative' }}>
              {isFetching && <LoadMask relative />}
              <div className="form__header">
                <h4 className="checkout__title">Checkout</h4>
              </div>
              <div className="checkout__bill">
                <h4 className="checkout__subtitle">{params.productName}</h4>
                <div className="checkout__row">
                  <span className="checkout__description">Price:</span>
                  <span className="checkout__price">{` $${params.price}`}</span>
                </div>
                <div className="checkout__row">
                  <span className="checkout__description">Tax:</span>
                  <span className="checkout__price">{` $${params.tax}`}</span>
                </div>
                <div className="checkout__row">
                  <span className="checkout__description">Total:</span>
                  <span className="checkout__price">{` $${params.total}`}</span>
                </div>
              </div>

              <div className="checkout__form">
                <CheckoutForm
                  values={fields}
                  options={options}
                  actions={{ handleRemoveCard, handleCheckout }}
                />
              </div>
            </div>
          </Form>
        </CSSTransition>
      }
      sidebarContent={<Sidebar />}
    />
  );
};

export default Checkout;
